import React, { useState, useEffect } from 'react';
import { categoryService } from '../../firebase/categoryService';
import type { Category } from '../../types/game';
import { collection, getCountFromServer, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebase/config';
import { questionService, Question } from '../../firebase/questionService';

const emptyCategory = { id: '', name: '', description: '', createdAt: null, updatedAt: null };

const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form, setForm] = useState({
    name: '',
    isActive: true,
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [questionCounts, setQuestionCounts] = useState<{ [categoryId: string]: number }>({});
  const [difficultyCounts, setDifficultyCounts] = useState<{ [categoryId: string]: { easy: number; medium: number; hard: number } }>({});
  const [sortBy, setSortBy] = useState<{ column: string; desc: boolean }>({ column: 'name', desc: false });
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [categoryQuestions, setCategoryQuestions] = useState<{ [categoryId: string]: Question[] }>({});

  useEffect(() => {
    fetchCategories();
  }, []);

  async function fetchCategories() {
    setLoading(true);
    const cats = await categoryService.getCategories();
    setCategories(cats);
    setLoading(false);
    fetchQuestionCounts(cats);
  }

  async function fetchQuestionCounts(cats: Category[]) {
    const counts: { [categoryId: string]: number } = {};
    const diffCounts: { [categoryId: string]: { easy: number; medium: number; hard: number } } = {};
    
    await Promise.all(
      cats.map(async (cat) => {
        const q = query(
          collection(firestore, 'questions'),
          where('categoryId', '==', cat.id)
        );
        const snap = await getDocs(q);
        const questions = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Question[];
        
        counts[cat.id] = questions.length;
        diffCounts[cat.id] = {
          easy: questions.filter(q => q.difficulty === 'easy').length,
          medium: questions.filter(q => q.difficulty === 'medium').length,
          hard: questions.filter(q => q.difficulty === 'hard').length
        };
      })
    );
    setQuestionCounts(counts);
    setDifficultyCounts(diffCounts);
  }

  function openAddModal() {
    setEditingCategory(null);
    setForm({ name: '', isActive: true, description: '' });
    setModalOpen(true);
  }

  function openEditModal(category: Category) {
    setEditingCategory(category);
    setForm({
      name: category.name,
      isActive: category.isActive !== false,
      description: category.description || '',
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingCategory(null);
  }

  async function handleSave() {
    setSaving(true);
    const data = {
      name: form.name,
      isActive: form.isActive,
      description: form.description,
    };
    if (editingCategory && editingCategory.id) {
      await categoryService.updateCategory(editingCategory.id, data);
    } else {
      await categoryService.addCategory(data);
    }
    setSaving(false);
    closeModal();
    fetchCategories();
  }

  async function handleDelete() {
    if (deleteId) {
      await categoryService.deleteCategory(deleteId);
      setDeleteId(null);
      fetchCategories();
    }
  }

  async function handleExpandCategory(categoryId: string) {
    if (expandedCategoryId === categoryId) {
      setExpandedCategoryId(null);
      return;
    }
    setExpandedCategoryId(categoryId);
    if (!categoryQuestions[categoryId]) {
      const allQuestions = await questionService.getQuestions();
      setCategoryQuestions(prev => ({ ...prev, [categoryId]: allQuestions.filter(q => q.categoryId === categoryId) }));
    }
  }

  function isCategoryGameReady(categoryId: string): boolean {
    const counts = difficultyCounts[categoryId];
    if (!counts) return false;
    
    // A category is game ready if it has at least:
    // - 2 easy questions (for $100, $200)
    // - 2 medium questions (for $300, $400)
    // - 1 hard question (for $500)
    return counts.easy >= 2 && counts.medium >= 2 && counts.hard >= 1;
  }

  async function handleToggleActive(category: Category) {
    await categoryService.updateCategory(category.id, { isActive: !(category.isActive !== false) });
    fetchCategories();
  }

  // Sorting logic for all columns
  const sortedCategories = [...categories].sort((a, b) => {
    const getStatus = (cat: Category) => (cat.isActive !== false ? 'Active' : 'Inactive');
    const getGameReady = (cat: Category) => isCategoryGameReady(cat.id) ? 1 : 0;
    const getQuestions = (cat: Category) => questionCounts[cat.id] || 0;
    const getDifficulty = (cat: Category) => {
      const diff = difficultyCounts[cat.id];
      if (!diff) return 0;
      // Weighted sum: easy=1, medium=10, hard=100 (arbitrary for sorting)
      return diff.easy + diff.medium * 10 + diff.hard * 100;
    };
    let valA, valB;
    switch (sortBy.column) {
      case 'name':
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
        break;
      case 'status':
        valA = getStatus(a);
        valB = getStatus(b);
        break;
      case 'gameReady':
        valA = getGameReady(a);
        valB = getGameReady(b);
        break;
      case 'questions':
        valA = getQuestions(a);
        valB = getQuestions(b);
        break;
      case 'difficulty':
        valA = getDifficulty(a);
        valB = getDifficulty(b);
        break;
      default:
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
    }
    if (valA < valB) return sortBy.desc ? 1 : -1;
    if (valA > valB) return sortBy.desc ? -1 : 1;
    return 0;
  });

  const handleSort = (column: string) => {
    setSortBy(prev => ({
      column,
      desc: prev.column === column ? !prev.desc : false,
    }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-brand-yellow">Categories</h2>
        <button onClick={openAddModal} className="bg-brand-yellow text-black font-bold px-6 py-2 rounded-lg hover:bg-[#fccd6c]/90 transition-colors">
          + Add Category
        </button>
      </div>
      {loading ? (
        <div className="text-brand-yellow">Loading...</div>
      ) : (
        <table className="w-full bg-[#222] rounded-xl overflow-hidden shadow-lg">
          <thead>
            <tr className="bg-[#333] text-brand-yellow">
              <th className="py-3 px-4 text-left cursor-pointer select-none" onClick={() => handleSort('name')}>
                Name {sortBy.column === 'name' && (sortBy.desc ? '▼' : '▲')}
              </th>
              <th className="py-3 px-4 text-center cursor-pointer select-none" onClick={() => handleSort('status')}>
                Status {sortBy.column === 'status' && (sortBy.desc ? '▼' : '▲')}
              </th>
              <th className="py-3 px-4 text-center cursor-pointer select-none" onClick={() => handleSort('gameReady')}>
                Game Ready {sortBy.column === 'gameReady' && (sortBy.desc ? '▼' : '▲')}
              </th>
              <th className="py-3 px-4 text-center cursor-pointer select-none" onClick={() => handleSort('questions')}>
                Questions {sortBy.column === 'questions' && (sortBy.desc ? '▼' : '▲')}
              </th>
              <th className="py-3 px-4 text-center cursor-pointer select-none" onClick={() => handleSort('difficulty')}>
                Difficulty Distribution {sortBy.column === 'difficulty' && (sortBy.desc ? '▼' : '▲')}
              </th>
              <th className="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedCategories.map(category => (
              <React.Fragment key={category.id}>
                <tr className="border-b border-[#444] hover:bg-[#292929]">
                  <td className="py-3 px-4 font-bold text-white cursor-pointer hover:underline" onClick={() => handleExpandCategory(category.id)}>
                    {category.name}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => handleToggleActive(category)}
                      className={`px-3 py-1 rounded-full text-sm font-bold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-yellow/50 ${category.isActive !== false ? 'bg-green-500/20 text-green-400 hover:bg-green-600/30' : 'bg-red-500/20 text-red-400 hover:bg-red-600/30'}`}
                      title="Toggle Active/Inactive"
                      type="button"
                    >
                      {category.isActive !== false ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {isCategoryGameReady(category.id) ? (
                      <span className="text-2xl" title="Category is ready for a game">✅</span>
                    ) : (
                      <span className="text-2xl" title="Category needs more questions">⚠️</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center text-brand-yellow font-bold">{questionCounts[category.id] !== undefined ? questionCounts[category.id] : '-'}</td>
                  <td className="py-3 px-4 text-center">
                    {difficultyCounts[category.id] && (
                      <div className="flex gap-2 justify-center text-sm">
                        <span className="text-green-400" title="Easy ($100, $200)">🟢 {difficultyCounts[category.id].easy}</span>
                        <span className="text-yellow-400" title="Medium ($300, $400)">🟡 {difficultyCounts[category.id].medium}</span>
                        <span className="text-red-400" title="Hard ($500)">🔴 {difficultyCounts[category.id].hard}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button onClick={() => openEditModal(category)} className="text-brand-yellow font-bold mr-4 hover:underline">Edit</button>
                    <button onClick={() => setDeleteId(category.id)} className="text-red-400 font-bold hover:underline">Delete</button>
                  </td>
                </tr>
                {expandedCategoryId === category.id && (
                  <tr>
                    <td colSpan={6} className="bg-[#181818] px-4 py-3">
                      <div>
                        <div className="font-bold text-brand-yellow mb-2">Questions in this category:</div>
                        {categoryQuestions[category.id] && categoryQuestions[category.id].length > 0 ? (
                          <ul className="list-disc ml-6">
                            {categoryQuestions[category.id].map(q => (
                              <li key={q.id} className="text-white mb-1">
                                <span className={`inline-block w-4 h-4 rounded-full mr-2 ${
                                  q.difficulty === 'easy' ? 'bg-green-400' :
                                  q.difficulty === 'medium' ? 'bg-yellow-400' :
                                  'bg-red-400'
                                }`}></span>
                                {q.text}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-gray-400">No questions in this category.</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal for Add/Edit Category */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#222] rounded-xl shadow-2xl p-8 max-w-md w-full relative">
            <button
              className="absolute top-4 right-4 text-brand-yellow text-2xl font-bold hover:text-white"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="text-2xl font-bold mb-6 text-brand-yellow">{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
            <div className="mb-4">
              <label className="block text-brand-yellow font-bold mb-2">Category Name</label>
              <input
                className="w-full bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Enter category name"
                disabled={saving}
              />
            </div>
            <div className="mb-4">
              <label className="block text-brand-yellow font-bold mb-2">Description <span className='text-red-400'>*</span></label>
              <textarea
                className="w-full bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Enter category description"
                disabled={saving}
                required
              />
            </div>
            <div className="mb-6">
              <label className="flex items-center gap-2 text-brand-yellow font-bold">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-brand-yellow/30 focus:ring-brand-yellow"
                  disabled={saving}
                />
                Active
              </label>
            </div>
            <div className="flex gap-4 justify-end">
              <button
                className="bg-[#5d7763] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#5d7763]/90 transition-colors"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="bg-brand-yellow text-black font-bold px-6 py-3 rounded-lg hover:bg-[#fccd6c]/90 transition-colors disabled:opacity-50"
                onClick={handleSave}
                disabled={saving || !form.name.trim() || !form.description.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#222] rounded-xl shadow-2xl p-8 max-w-md w-full relative">
            <h3 className="text-2xl font-bold mb-6 text-brand-yellow">Delete Category</h3>
            <p className="text-white mb-8">Are you sure you want to delete this category? This cannot be undone.</p>
            <div className="flex gap-4 justify-end">
              <button
                className="bg-[#5d7763] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#5d7763]/90 transition-colors"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white font-bold px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories; 